import {BehaviourSeverity} from "@shared/api/models/query/patient-statistics.model";

// ── Shared types used by the weekly snapshot model & weekly components ────────

export type TrendDirection = 'up' | 'down' | 'same';

export type EmotionCategory =
    | 'Happy' | 'Surprised' | 'Disgusted' | 'Angry' | 'Bad' | 'Fearful' | 'Sad';

// ── Daily check-ins v2 (UNIVAPLAT-2359 v2 daily statistics payload) ───────────
// GET /api/v2/patients/:id/statistics/daily?date=YYYYMMDD[&type=carer]
// The legacy snapshot types (PatientDailySnapshot, DailyCheckinsBlock,
// DailyAdherence*, DailyFoodEntry, DailyDiaryEntry, …) were removed by the
// UNIVAPLAT-2382 cleanup sub-task once every consumer migrated to this payload.

export interface DailySleepV2 {
    /** "Energised" | "Refreshed" | "Okay" | "Tired" | "Exhausted";
     *  null on legacy-era days (old SQU mapping) — render factors without a chip. */
    feltState: string | null;
    factors: string[];
    /** Whether the sleep section was answered — NOT simply "feltState !== null": a legacy SQU
     *  day completed with zero factors selected is answered despite feltState always being null
     *  in the legacy era, and (carer) sleep answered via UDC-C's skip escape hatch is answered
     *  despite having no feltState value. Computed server-side by the same
     *  CheckinCompletionResolver the weekly Timeline uses, so the two views can't disagree. */
    answered: boolean;
}

export interface DailyStressV2 {
    /** 0-100 score; null when the stress bit was not completed or was explicitly skipped. */
    value: number | null;
    /** Word band ("noticeable" | "pressing" | "heavy" | …); null with value null. */
    label: string | null;
    /** Whether the stress section was answered — true for a real value OR an explicit "skip"
     *  escape-hatch answer (per the approved Confluence decision: a skipped section counts as
     *  completed), false only when the section was never touched at all. */
    answered: boolean;
}

export interface DailyMoodV2 {
    /** Core emotion (colour-coded chip); null when no mood check was completed, or the mood
     *  question was explicitly skipped, that day. */
    core: string | null;
    /** Optional finer word; absent/null on legacy YEM-mapped days — chip shows core only. */
    fine?: string | null;
    /** Whether the mood section was answered — true for a real core value OR an explicit
     *  "skip" escape-hatch answer, false only when the section was never touched at all. */
    answered: boolean;
}

export interface DailyCheckinsV2 {
    sleep: DailySleepV2;
    stress: DailyStressV2;
    mood: DailyMoodV2;
    /** Patient-only — the carer (?type=carer) payload omits this member. */
    contributingFactors?: string[];
}

export interface DailyBehaviourV2 {
    name: string;
    counter: number;
    severity: BehaviourSeverity;
    /** Compares today's counter to the same behaviour's counter in yesterday's snapshot
     *  (UNIVAPLAT-2359 v2 daily payload contract); null when there's no comparable
     *  previous-day data. Scoped to the Daily Summary Behaviour log only — the Timeline
     *  (dashboard) behaviour pills do not render trend. */
    trend: TrendDirection | null;
    /** 0-100 integer, already rounded and clamped server-side (UNIVAPLAT-2359/UNIVAPLAT-3031
     *  contract) — today's counter against this behaviour's per-behaviour daily cap
     *  (laxative/diuretics/purge/binge = 20, skip = 6, exercise = 24). Scoped to the Daily
     *  Summary Behaviour log only — the weekly Behaviour log and the Timeline (dashboard)
     *  behaviour pills do not render a percentage/progress bar. */
    percentage: number;
}

export interface DailyChapterV2 {
    id: number;
    displayTitlePortal: string;
    code: string;
    progressValue: number;
}

export interface DailySummaryV2 {
    computedAt: string;
    dailyCheckins: DailyCheckinsV2;
    behaviours: DailyBehaviourV2[];
    chapters: DailyChapterV2[];
}

/** No-snapshot payload — the endpoint body is {"data": {"data": null, "noData": true}},
 *  so after the service unwraps the outer envelope the caller receives this object. */
export interface DailyNoSnapshotV2 {
    data: null;
    noData: true;
}

/** Unwrapped result of GET …/statistics/daily (v2): either the summary or a typed no-data marker. */
export type DailySummaryV2Response = DailySummaryV2 | DailyNoSnapshotV2;

export function isDailyNoSnapshotV2(
    response: DailySummaryV2Response | null | undefined
): response is DailyNoSnapshotV2 {
    return !!response && 'noData' in response && response.noData === true;
}
